import dayjs from 'dayjs'

import { cn } from '@/app/utils'
import { useServerTimeStore } from '@/components/entities/common/model/server-time'
import { DEFAULT_TIMEZONE } from '@/components/entities/requests/model/constants'
import {
  getSlotPosition,
  mergeSlots,
  RowBoundary,
  Slot
} from '@/components/shared/calendar/lib/slotPosition'
import { toMskCalendarDay } from '@/utils/datetime'

import { getFirstBookableHour } from '../utils/getFirstBookableHour'
import { DateRange } from './Calendar.types'

const hoursArray = Array.from({ length: 24 }, (_, index) => {
  const nextHour = index + 1
  return `${index.toString().padStart(2, '0')}-${nextHour
    .toString()
    .padStart(2, '0')}`
})

const HOURS_PER_ROW = 4

const isHoursRowBoundary = (index: number): RowBoundary => ({
  isRowStart: index % HOURS_PER_ROW === 0,
  isRowEnd: index % HOURS_PER_ROW === HOURS_PER_ROW - 1
})

type Props = {
  range?: DateRange
  setRange: (range?: DateRange) => void
  monthYear: Date
  cellClassName?: string
  individualSlots?: {
    bordered: Slot[]
    disabled: Slot[]
  }
}

// /slots запрашивается отдельно на каждый день, поэтому слот, чей конец
// (в локальном времени) уходит за полночь на следующий день, там уже не
// вернётся - обрезаем такие слоты по границам текущего дня, чтобы последний
// час всё равно закрашивался как конец диапазона
const clipSlotsToDay = (
  slots: Slot[] | undefined,
  dayStart: dayjs.Dayjs,
  dayEnd: dayjs.Dayjs
): Slot[] =>
  (slots ?? []).reduce<Slot[]>((acc, slot) => {
    const start = dayjs(slot.from).tz(DEFAULT_TIMEZONE).startOf('hour')
    const end = dayjs(slot.to).tz(DEFAULT_TIMEZONE).startOf('hour')
    if (end.isBefore(dayStart) || start.isAfter(dayEnd)) {
      return acc
    }

    const clippedStart = start.isBefore(dayStart) ? dayStart : start
    const clippedEnd = end.isAfter(dayEnd) ? dayEnd : end

    if (clippedStart.isSame(clippedEnd) && !start.isSame(end)) {
      return acc
    }

    acc.push({
      from: clippedStart.toISOString(),
      to: clippedEnd.toISOString()
    })

    return acc
  }, [])

const positionClasses = {
  start: '!rounded-r-0 !rounded-l-6',
  end: '!rounded-l-0 !rounded-r-6',
  middle: '!rounded-0',
  single: '!rounded-6'
}

const getBorderedSlotClasses = (
  borderedPosition: keyof typeof positionClasses | null
) =>
  cn(
    borderedPosition && '!border-y !border-bronze-40',
    borderedPosition === 'start' && '!border-l !border-r-0',
    borderedPosition === 'end' && '!border-l-0 !border-r',
    borderedPosition === 'middle' && '!border-l-0 !border-r-0',
    borderedPosition === 'single' && '!border-l !border-r'
  )

const getRangeSelectionClasses = ({
  isStart,
  isEnd,
  isInRange,
  isOccupied,
  rangeFrom,
  rangeTo
}: {
  isStart: boolean
  isEnd: boolean
  isInRange: boolean
  isOccupied: boolean
  rangeFrom: dayjs.Dayjs | null
  rangeTo: dayjs.Dayjs | null
}) =>
  cn(
    (isStart || isEnd) && 'bg-bronze-40 text-white',
    isInRange && '!rounded-0 bg-grey-10',
    isInRange && (isOccupied ? 'text-red-70' : 'text-grey-80'),
    (isStart || isEnd) && isOccupied && '!text-red-70',
    isStart && !isEnd && rangeTo && 'rounded-e-0',
    isEnd && !isStart && rangeFrom && 'rounded-s-0'
  )

export const HoursPicker = ({
  range = { from: undefined, to: undefined },
  setRange,
  monthYear,
  cellClassName,
  individualSlots
}: Props) => {
  const serverOffsetMs = useServerTimeStore(state => state.serverOffsetMs)
  const dayStart = toMskCalendarDay(monthYear).startOf('day')
  const dayEnd = dayStart.add(23, 'hour')

  const now = dayjs(Date.now() + serverOffsetMs).tz(DEFAULT_TIMEZONE)
  const isToday = dayStart.format('YYYY-MM-DD') === now.format('YYYY-MM-DD')
  const currentHour = now.startOf('hour')

  const firstBookableHour = getFirstBookableHour(Date.now() + serverOffsetMs)

  const hasBlockedHours = isToday && firstBookableHour.isAfter(dayStart)

  const effectiveDayStart = hasBlockedHours ? firstBookableHour : dayStart

  const borderedSlots = mergeSlots(
    clipSlotsToDay(individualSlots?.bordered, effectiveDayStart, dayEnd),
    'hour',
    DEFAULT_TIMEZONE
  )
  const disabledSlots = mergeSlots(
    clipSlotsToDay(individualSlots?.disabled, effectiveDayStart, dayEnd),
    'hour',
    DEFAULT_TIMEZONE
  )
  const rangeFrom = range.from
    ? dayjs(range.from).tz(DEFAULT_TIMEZONE).startOf('hour')
    : null

  const rangeTo = range.to
    ? dayjs(range.to).tz(DEFAULT_TIMEZONE).startOf('hour').subtract(1, 'hour')
    : null

  const pastHoursSlots = hasBlockedHours
    ? [
        {
          from: dayStart.toISOString(),
          to: firstBookableHour.subtract(1, 'hour').toISOString()
        }
      ]
    : []

  const isHourOccupied = (hourDate: dayjs.Dayjs) =>
    getSlotPosition(
      hourDate,
      borderedSlots,
      'hour',
      undefined,
      DEFAULT_TIMEZONE
    ) !== null ||
    getSlotPosition(
      hourDate,
      disabledSlots,
      'hour',
      undefined,
      DEFAULT_TIMEZONE
    ) !== null

  const hasOccupiedHourBetween = (from: dayjs.Dayjs, to: dayjs.Dayjs) => {
    let cursor = from.add(1, 'hour')

    while (cursor.isBefore(to)) {
      if (isHourOccupied(cursor)) {
        return true
      }
      cursor = cursor.add(1, 'hour')
    }

    return false
  }

  const handleHourClick = (hour: number) => {
    const clickedHour = dayStart.add(hour, 'hour')

    const currentFrom = range.from
      ? dayjs(range.from).tz(DEFAULT_TIMEZONE)
      : null
    const currentTo = range.to ? dayjs(range.to).tz(DEFAULT_TIMEZONE) : null

    const canExtend =
      currentFrom && currentTo && currentTo.diff(currentFrom, 'hour') === 1

    if (!currentFrom || !canExtend) {
      setRange({
        from: clickedHour.toDate(),
        to: clickedHour.add(1, 'hour').toDate()
      })
      return
    }

    if (clickedHour.isSame(currentFrom, 'hour')) {
      setRange(undefined)
      return
    }

    const [from, to] = clickedHour.isBefore(currentFrom)
      ? [clickedHour, currentFrom]
      : [currentFrom, clickedHour]

    // диапазон нельзя продлить через занятый час - вместо этого начинаем
    // выбор заново с кликнутого часа
    if (hasOccupiedHourBetween(from, to)) {
      setRange({
        from: clickedHour.toDate(),
        to: clickedHour.add(1, 'hour').toDate()
      })
      return
    }

    setRange({ from: from.toDate(), to: to.add(1, 'hour').toDate() })
  }

  return (
    <div className="grid grid-cols-4">
      {hoursArray.map((label, index) => {
        const hourDate = dayStart.add(index, 'hour')
        const isStart = rangeFrom?.isSame(hourDate, 'hour') ?? false
        const isEnd = rangeTo?.isSame(hourDate, 'hour') ?? false
        const isInRange =
          rangeFrom &&
          rangeTo &&
          hourDate.isAfter(rangeFrom, 'hour') &&
          hourDate.isBefore(rangeTo, 'hour')

        const pastPosition = getSlotPosition(
          hourDate,
          pastHoursSlots,
          'hour',
          undefined,
          DEFAULT_TIMEZONE
        )
        const isPastHour = pastPosition !== null

        const isRangeSelected = Boolean(isStart || isEnd || isInRange)
        const isCurrentHour = isToday && hourDate.isSame(currentHour, 'hour')

        const borderedPosition =
          isPastHour || isRangeSelected
            ? null
            : getSlotPosition(
                hourDate,
                borderedSlots,
                'hour',
                () => isHoursRowBoundary(index),
                DEFAULT_TIMEZONE
              )
        const disabledPosition =
          isPastHour || isRangeSelected
            ? null
            : getSlotPosition(
                hourDate,
                disabledSlots,
                'hour',
                undefined,
                DEFAULT_TIMEZONE
              )

        const isOccupied =
          getSlotPosition(
            hourDate,
            borderedSlots,
            'hour',
            undefined,
            DEFAULT_TIMEZONE
          ) !== null ||
          getSlotPosition(
            hourDate,
            disabledSlots,
            'hour',
            undefined,
            DEFAULT_TIMEZONE
          ) !== null

        // занятый (disabled/bordered) слот нельзя выбрать - но если он уже часть
        // текущего выбранного диапазона (пришёл извне), не блокируем саму ячейку
        const isBlockedOccupied = isOccupied && !isRangeSelected

        return (
          <button
            key={index}
            type="button"
            disabled={isPastHour || isBlockedOccupied}
            className={cn(
              'h-34 w-70 rounded-6 capitalize !text-16 xxs:w-full',
              'hover:bg-bronze-40 hover:text-white',
              getBorderedSlotClasses(borderedPosition),
              disabledPosition && 'bg-bronze-10 text-grey-100',
              borderedPosition
                ? positionClasses[borderedPosition]
                : disabledPosition && positionClasses[disabledPosition],
              !isPastHour &&
                !isRangeSelected &&
                !isOccupied &&
                isCurrentHour &&
                'text-bronze-40',
              isPastHour &&
                'cursor-not-allowed text-grey-30 opacity-100 hover:bg-grey-10 hover:text-grey-100',
              isPastHour && pastPosition && positionClasses[pastPosition],
              !isPastHour &&
                isBlockedOccupied &&
                'hover:bg-inherit hover:text-inherit cursor-not-allowed',
              !isPastHour &&
                getRangeSelectionClasses({
                  isStart,
                  isEnd,
                  isInRange: Boolean(isInRange),
                  isOccupied,
                  rangeFrom,
                  rangeTo
                }),
              cellClassName
            )}
            onClick={() => handleHourClick(index)}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
